import { useState, useEffect } from 'react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { getHostBookings, approveBooking, declineBooking } from '../../lib/api';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { Calendar, Check, X, ArrowRight, Loader2 } from 'lucide-react';

export default function HostReservations() {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [nextCursor, setNextCursor] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const [actionLoading, setActionLoading] = useState(null);

  const loadBookings = async (cursor = null, status = statusFilter) => {
    try {
      const params = { limit: 20, cursor };
      if (status) params.status = status;
      
      const response = await getHostBookings(params);
      
      if (cursor) {
        setBookings(prev => [...prev, ...response.data.items]);
      } else {
        setBookings(response.data.items || []);
      }
      
      setNextCursor(response.data.next_cursor);
      setHasMore(response.data.has_more);
    } catch (error) {
      console.error('Failed to load bookings:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadBookings();
  }, []);

  const handleStatusChange = (status) => {
    setStatusFilter(status);
    setLoading(true);
    loadBookings(null, status);
  };

  const handleApprove = async (bookingId) => {
    setActionLoading(bookingId);
    try {
      await approveBooking(bookingId);
      setBookings(prev => 
        prev.map(b => b.id === bookingId ? { ...b, status: 'approved' } : b)
      );
      toast.success('Booking approved! Guest can now complete payment.');
    } catch (error) {
      toast.error('Failed to approve booking');
    } finally {
      setActionLoading(null);
    }
  };

  const handleDecline = async (bookingId) => {
    setActionLoading(bookingId);
    try {
      await declineBooking(bookingId);
      setBookings(prev => 
        prev.map(b => b.id === bookingId ? { ...b, status: 'cancelled' } : b)
      );
      toast.success('Booking declined');
    } catch (error) {
      toast.error('Failed to decline booking');
    } finally {
      setActionLoading(null);
    }
  };

  const formatPrice = (cents) => {
    return (cents / 100).toLocaleString('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0
    });
  };

  const getStatusBadge = (status) => {
    const variants = {
      pending: 'bg-amber-100 text-amber-700',
      approved: 'bg-blue-100 text-blue-700',
      paid: 'bg-green-100 text-green-700',
      completed: 'bg-slate-100 text-slate-700',
      cancelled: 'bg-slate-100 text-slate-500',
      refunded: 'bg-purple-100 text-purple-700'
    };
    return variants[status] || 'bg-slate-100 text-slate-700';
  };

  return (
    <div className="space-y-6" data-testid="host-reservations">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-2xl font-bold text-slate-900">Reservations</h1>
          <p className="text-slate-500 mt-1">Manage booking requests and reservations</p>
        </div>
        <Select value={statusFilter} onValueChange={handleStatusChange}>
          <SelectTrigger className="w-40">
            <SelectValue placeholder="All statuses" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            <SelectItem value="pending">Pending</SelectItem>
            <SelectItem value="approved">Approved</SelectItem>
            <SelectItem value="paid">Paid</SelectItem>
            <SelectItem value="completed">Completed</SelectItem>
            <SelectItem value="cancelled">Cancelled</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      ) : bookings.length > 0 ? (
        <>
          <div className="space-y-4">
            {bookings.map((booking) => (
              <Card key={booking.id}>
                <CardContent className="p-6">
                  <div className="flex items-start justify-between">
                    <div className="space-y-2">
                      <div className="flex items-center gap-3">
                        <h3 className="font-medium text-slate-900 text-lg">
                          {booking.property_title}
                        </h3>
                        <Badge className={getStatusBadge(booking.status)}>
                          {booking.status}
                        </Badge>
                      </div>
                      
                      <div className="text-slate-600">
                        <p className="font-medium">{booking.guest_name}</p>
                        <p className="text-sm text-slate-500">{booking.guest_email}</p>
                      </div>
                      
                      <div className="flex items-center gap-4 text-sm text-slate-500">
                        <span className="flex items-center gap-1">
                          <Calendar className="w-4 h-4" />
                          {format(new Date(booking.check_in), 'MMM d')} - {format(new Date(booking.check_out), 'MMM d, yyyy')}
                        </span>
                        <span>{booking.nights} nights</span>
                        <span>{booking.guests_count} guests</span>
                      </div>
                    </div>

                    <div className="text-right">
                      <p className="text-2xl font-bold text-slate-900">
                        {formatPrice(booking.total_charged)}
                      </p>
                      <p className="text-sm text-slate-500">
                        Host fee: {formatPrice(booking.host_fee_snapshot)}
                      </p>
                      <p className="text-sm font-medium text-green-600">
                        You earn: {formatPrice(booking.total_charged - booking.guest_fee_snapshot - booking.host_fee_snapshot)}
                      </p>
                    </div>
                  </div>

                  {booking.status === 'pending' && (
                    <div className="flex gap-2 mt-4 pt-4 border-t border-slate-100">
                      <Button
                        onClick={() => handleApprove(booking.id)}
                        disabled={actionLoading === booking.id}
                        className="btn-primary"
                        data-testid={`approve-btn-${booking.id}`}
                      >
                        {actionLoading === booking.id ? (
                          <Loader2 className="w-4 h-4 animate-spin mr-2" />
                        ) : (
                          <Check className="w-4 h-4 mr-2" />
                        )}
                        Approve
                      </Button>
                      <Button
                        variant="outline"
                        onClick={() => handleDecline(booking.id)}
                        disabled={actionLoading === booking.id}
                        data-testid={`decline-btn-${booking.id}`}
                      >
                        <X className="w-4 h-4 mr-2" />
                        Decline
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>

          {hasMore && (
            <div className="text-center">
              <Button 
                variant="outline"
                onClick={() => loadBookings(nextCursor)}
              >
                Load More
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          )}
        </>
      ) : (
        <Card>
          <CardContent className="p-12 text-center">
            <Calendar className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <h3 className="font-heading text-xl font-semibold text-slate-900 mb-2">
              No reservations yet
            </h3>
            <p className="text-slate-500">
              {statusFilter 
                ? `No ${statusFilter} bookings found.` 
                : 'Your booking requests will appear here.'}
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
